import {startCase} from "lodash";
import {NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {nodeLabel, removeDefaultService} from "../../default-service";
import {ApplyTask, runApplyTasks} from "../../run-apply-tasks";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PlayCommandRuntimeInfo} from "./command-definitions";
import {prepareDiffCommand} from "./prepare-diff-command";
import {getNodeByIdOrFQCN} from "../../../cody-wizard/node/get-node-by-id-or-fqcn";

export class CommandRemoved implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'CommandRemoved';
  public readonly operation: PlayshotChangedOperation = 'removed';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.command;
  public readonly diff: PlayshotChangedDiff;

  constructor(name: string, nodeId: string, command: PlayCommandRuntimeInfo, oldPlayshot: Playshot) {

    const rules = oldPlayshot.playConfig.commandHandlers[command.desc.name] || [];

    this.name = name;
    this.nodeId = nodeId;
    this.diff = {
      new: null,
      old: prepareDiffCommand(command, rules),
    };
  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = getNodeByIdOrFQCN(this.nodeId, this.name, NodeType.command, graph, elementsTree);

    if(!existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the command cannot be found on the board.`,
        details: `There is no element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }

    const similarNodes = elementsTree.getSimilarElements(existingNode);

    const applyTasks: ApplyTask[] = [
      {
        name: "Focus command to remove",
        func: async () => {
          graph.focusNode(existingNode);
          return {
            cody: ``,
            type: CodyResponseType.Empty
          }
        }
      },
      {
        name: "Remove command",
        func: async () => {
          return new Promise(resolve => {
            window.setTimeout(() => {
              graph.deleteNode(existingNode);

              resolve({
                cody: `I've removed the command card for "${nodeLabel(this.name)}" from the board`
              });
            }, 1500);
          })
        }
      }
    ];

    similarNodes.forEach(sN => {
      applyTasks.push({
        name: "Focus command to remove",
        func: async () => {
          graph.focusNode(sN);
          return {
            cody: ``,
            type: CodyResponseType.Empty
          }
        }
      });
      applyTasks.push({
        name: "Remove command",
        func: async () => {
          return new Promise(resolve => {
            window.setTimeout(() => {
              graph.deleteNode(sN);

              resolve({
                cody: ``,
                type: CodyResponseType.Empty
              });
            }, 1500);
          })
        }
      })
    })

    return runApplyTasks(applyTasks)
  }
}
